import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Cpu, Loader2, ToggleLeft, ToggleRight, Search, RefreshCw, AlertTriangle, Check, Square, CheckSquare, X, Download, ChevronUp, ChevronDown } from 'lucide-react';
import { API } from '../../App';

const TIER_COLORS = { free: '#22C55E', budget: '#00D4FF', standard: '#F2A900', premium: '#FF4D00' };
const TIER_ORDER = ['free', 'budget', 'standard', 'premium'];
const TIER_RANK = { free: 0, budget: 1, standard: 2, premium: 3 };
const NEW_THRESHOLD_DAYS = 30;

function fmtK(n) { return n ? `${Math.round(n / 1000)}k` : '—'; }
function fmtIq(s) { return s != null ? (s / 10).toFixed(1) : '—'; }
// #1162: format OR prices stored as $/token to a compact $/M-token string.
function fmtPriceM(perToken) {
  if (perToken == null || perToken === 0) return '—';
  const perMillion = Number(perToken) * 1_000_000;
  if (perMillion < 1) return perMillion.toFixed(2);
  if (perMillion < 10) return perMillion.toFixed(1);
  return Math.round(perMillion).toString();
}
function fmtPriceTitle(perToken) {
  if (perToken == null || perToken === 0) return 'Free';
  const perMillion = Number(perToken) * 1_000_000;
  return `$${perMillion.toFixed(4)} per million tokens`;
}
function isNew(m) {
  const d = m.or_created_at || m.created_at;
  if (!d) return false;
  return (Date.now() - new Date(d).getTime()) < NEW_THRESHOLD_DAYS * 86400000;
}

export default function ModelGovernance({ wsId }) {
  const [enabled, setEnabled] = useState(false);
  const [allowlist, setAllowlist] = useState([]);
  const [deprecatedCount, setDeprecatedCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [roster, setRoster] = useState([]);
  const [rosterLoading, setRosterLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [tierTab, setTierTab] = useState(''); // '' = all
  const [allowlistTierTab, setAllowlistTierTab] = useState(''); // '' = all

  const [syncStatus, setSyncStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);

  const [selected, setSelected] = useState(new Set());
  const [adding, setAdding] = useState(false);

  const [minIq, setMinIq] = useState(0);
  const [minCtx, setMinCtx] = useState(0);
  const [minOut, setMinOut] = useState(0);
  const [hasIqOnly, setHasIqOnly] = useState(false);
  const [newOnly, setNewOnly] = useState(false);
  const [capFilter, setCapFilter] = useState('');

  // Sortable columns (parity with admin Model Roster)
  const [sortField, setSortField] = useState(null);
  const [sortDir, setSortDir] = useState('asc');

  const load = useCallback(() => {
    if (!wsId) return;
    setLoading(true);
    API.get(`/api/workspaces/${wsId}/model-governance`)
      .then(r => {
        setEnabled(r.data.model_governance_enabled || false);
        setAllowlist(r.data.allowlist || []);
        setDeprecatedCount(r.data.deprecated_count || 0);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [wsId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    API.get('/api/models/sync-status').then(r => setSyncStatus(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!enabled) return;
    setRosterLoading(true);
    API.get('/api/models/roster?active_only=false')
      .then(r => setRoster(r.data.models || []))
      .catch(() => setRoster([]))
      .finally(() => setRosterLoading(false));
  }, [enabled]);

  const toggle = async () => {
    try {
      await API.put(`/api/workspaces/${wsId}/model-governance`, { enabled: !enabled });
      setEnabled(!enabled);
    } catch (e) { setError(e.response?.data?.detail || 'Failed'); }
  };

  const doSync = async () => {
    setSyncing(true); setSyncResult(null); setError('');
    try {
      const r = await API.post('/api/admin/models/sync');
      setSyncResult(r.data);
      API.get('/api/models/roster?active_only=false').then(r2 => setRoster(r2.data.models || []));
      API.get('/api/models/sync-status').then(r2 => setSyncStatus(r2.data));
      load();
    } catch (e) { setError(e.response?.data?.detail || 'Sync failed'); }
    finally { setSyncing(false); }
  };

  const addSelected = async () => {
    if (selected.size === 0) return;
    setAdding(true); setError('');
    const modelsToAdd = filteredRoster.filter(m => selected.has(m.slug));
    for (const m of modelsToAdd) {
      try {
        await API.post(`/api/workspaces/${wsId}/model-governance/allowlist`, {
          model_id: m.slug, provider: m.provider || m.slug.split('/')[0]
        });
      } catch {} // Skip duplicates (409)
    }
    setSelected(new Set());
    setAdding(false);
    load();
  };

  const removeModel = async (entryId) => {
    await API.delete(`/api/workspaces/${wsId}/model-governance/allowlist/${entryId}`);
    load();
  };

  const toggleSelect = (slug) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(slug)) next.delete(slug); else next.add(slug);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selected.size === filteredRoster.length) setSelected(new Set());
    else setSelected(new Set(filteredRoster.map(m => m.slug)));
  };

  // Sorting (parity with admin Model Roster)
  const applySort = useCallback((list) => {
    if (!sortField) return list;
    return [...list].sort((a, b) => {
      let av, bv;
      if (sortField === 'display_name') { av = (a.display_name || a.slug || '').toLowerCase(); bv = (b.display_name || b.slug || '').toLowerCase(); }
      else if (sortField === 'tier') { av = TIER_RANK[a.tier] ?? 99; bv = TIER_RANK[b.tier] ?? 99; }
      else if (sortField === 'intelligence_score') { av = a.intelligence_score ?? -1; bv = b.intelligence_score ?? -1; }
      else if (sortField === 'context_window') { av = a.context_window ?? 0; bv = b.context_window ?? 0; }
      else if (sortField === 'max_output_tokens') { av = a.max_output_tokens ?? 0; bv = b.max_output_tokens ?? 0; }
      else if (sortField === 'prompt_price_per_token') { av = Number(a.prompt_price_per_token ?? 0); bv = Number(b.prompt_price_per_token ?? 0); }
      else if (sortField === 'completion_price_per_token') { av = Number(a.completion_price_per_token ?? 0); bv = Number(b.completion_price_per_token ?? 0); }
      else if (sortField === 'or_created_at') {
        av = a.or_created_at ? new Date(a.or_created_at).getTime() : 0;
        bv = b.or_created_at ? new Date(b.or_created_at).getTime() : 0;
      }
      else return 0;
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
  }, [sortField, sortDir]);

  const toggleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };

  const allowlistSlugs = useMemo(() => new Set(allowlist.map(m => m.model_id)), [allowlist]);

  const filteredRoster = useMemo(() => applySort(
    roster.filter(m => {
      if (allowlistSlugs.has(m.slug)) return false;
      if (m.deprecated_at) return false;
      if (tierTab && m.tier !== tierTab) return false;
      if (hasIqOnly && m.intelligence_score == null) return false;
      if (newOnly && !isNew(m)) return false;
      if (minIq > 0 && (m.intelligence_score == null || m.intelligence_score < minIq)) return false;
      if (minCtx > 0 && (m.context_window == null || m.context_window < minCtx)) return false;
      if (minOut > 0 && (m.max_output_tokens == null || m.max_output_tokens < minOut)) return false;
      if (capFilter === 'thinking' && !m.supports_thinking) return false;
      if (capFilter === 'function_calling' && !m.supports_function_calling) return false;
      if (capFilter === 'images' && !m.supports_images) return false;
      if (search) {
        const s = search.toLowerCase();
        return (m.slug || '').toLowerCase().includes(s) ||
               (m.display_name || '').toLowerCase().includes(s) ||
               (m.provider || '').toLowerCase().includes(s);
      }
      return true;
    })
  ), [roster, allowlistSlugs, search, tierTab, hasIqOnly, newOnly, minIq, minCtx, minOut, capFilter, applySort]);

  // Tier counts for available models (excluding already-allowlisted)
  const availableTierCounts = useMemo(() => {
    const counts = {};
    roster.filter(m => !allowlistSlugs.has(m.slug) && !m.deprecated_at).forEach(m => {
      counts[m.tier] = (counts[m.tier] || 0) + 1;
    });
    return counts;
  }, [roster, allowlistSlugs]);

  // Tier counts for allowlist
  const allowlistTierCounts = useMemo(() => {
    const counts = {};
    allowlist.forEach(m => {
      const tier = m.tier || 'unknown';
      counts[tier] = (counts[tier] || 0) + 1;
    });
    return counts;
  }, [allowlist]);

  // #1043 — enrich allowlist rows with roster metadata so the Allowed table
  // can show the same ADDED/IQ/CTX/OUT columns and participate in the shared
  // sort state as the Available table (and admin Active Models).
  const rosterBySlug = useMemo(() => {
    const map = new Map();
    roster.forEach(m => { if (m.slug) map.set(m.slug, m); });
    return map;
  }, [roster]);

  const enrichedAllowlist = useMemo(() => (
    allowlist.map(m => {
      const r = rosterBySlug.get(m.model_id) || {};
      return {
        ...m,
        intelligence_score: m.intelligence_score ?? r.intelligence_score ?? null,
        context_window: m.context_window ?? r.context_window ?? null,
        max_output_tokens: m.max_output_tokens ?? r.max_output_tokens ?? null,
        // #1162: pricing falls back to roster row when allowlist API doesn't
        // denormalize it (typical case — pricing isn't on workspace_model_allowlist).
        prompt_price_per_token: m.prompt_price_per_token ?? r.prompt_price_per_token ?? null,
        completion_price_per_token: m.completion_price_per_token ?? r.completion_price_per_token ?? null,
        or_created_at: m.or_created_at ?? r.or_created_at ?? null,
        // `display_name` is the sort key for the MODEL column — fall back to
        // the roster's display name then the model slug so sort works even
        // when the allowlist API hasn't denormalized it.
        display_name: m.display_name || r.display_name || m.model_id,
      };
    })
  ), [allowlist, rosterBySlug]);

  // Filtered + sorted allowlist — reuses the shared applySort so clicking a
  // column header affects both tables consistently.
  const filteredAllowlist = useMemo(() => (
    applySort(
      allowlistTierTab
        ? enrichedAllowlist.filter(m => m.tier === allowlistTierTab)
        : enrichedAllowlist
    )
  ), [enrichedAllowlist, allowlistTierTab, applySort]);

  useEffect(() => { setSelected(new Set()); }, [search, tierTab, hasIqOnly, newOnly, minIq, minCtx, minOut, capFilter]);

  const formatTimeAgo = (iso) => {
    if (!iso) return 'Never';
    const diff = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  };

  // Tier tab component
  const TierTabs = ({ active, onSelect, counts }) => (
    <div className="flex gap-1 mb-3">
      <button onClick={() => onSelect('')}
        className={`px-2.5 py-1 text-[10px] font-mono transition-colors ${
          !active ? 'bg-[#FF4D00] text-white' : 'bg-[#0F0F0F] border border-[#2A2A2A] text-[#A0A0A0] hover:text-[#F5F1E8]'
        }`}>
        All ({Object.values(counts).reduce((a, b) => a + b, 0)})
      </button>
      {TIER_ORDER.filter(t => counts[t]).map(t => (
        <button key={t} onClick={() => onSelect(active === t ? '' : t)}
          className={`px-2.5 py-1 text-[10px] font-mono transition-colors ${
            active === t ? 'text-white' : 'bg-[#0F0F0F] border border-[#2A2A2A] text-[#A0A0A0] hover:text-[#F5F1E8]'
          }`}
          style={active === t ? { backgroundColor: TIER_COLORS[t] } : {}}>
          {t.charAt(0).toUpperCase() + t.slice(1)} ({counts[t] || 0})
        </button>
      ))}
    </div>
  );

  const SortTh = ({ field, label, className = '' }) => {
    const active = sortField === field;
    return (
      <button onClick={() => toggleSort(field)}
        className={`flex items-center gap-0.5 hover:text-[#F5F1E8] transition-colors ${active ? 'text-[#FF4D00]' : ''} ${className}`}>
        {label}
        {active ? (sortDir === 'asc' ? <ChevronUp className="w-2.5 h-2.5" /> : <ChevronDown className="w-2.5 h-2.5" />) : null}
      </button>
    );
  };

  if (loading) return <div className="flex items-center gap-2 text-[#A0A0A0] py-8"><Loader2 className="w-4 h-4 animate-spin" /> Loading...</div>;

  return (
    <div className="space-y-4">
      {/* Governance toggle */}
      <div className="flex items-center justify-between p-3 bg-[#0F0F0F] border border-[#2A2A2A]">
        <div>
          <p className="text-sm font-medium">Model Governance</p>
          <p className="text-xs text-[#A0A0A0]">When enabled, only allowlisted models can be used in workspace frays</p>
        </div>
        <button onClick={toggle} className="p-2 min-w-[44px] min-h-[44px] flex items-center justify-center">
          {enabled ? <ToggleRight className="w-6 h-6 text-[#FF4D00]" /> : <ToggleLeft className="w-6 h-6 text-[#A0A0A0]" />}
        </button>
      </div>

      {/* Sync controls */}
      <div className="flex items-center justify-between p-3 bg-[#0F0F0F] border border-[#2A2A2A]">
        <div className="flex flex-col gap-0.5">
          <p className="text-xs font-mono text-[#A0A0A0]">
            Models last updated: <span className="text-[#F5F1E8]">{formatTimeAgo(syncStatus?.last_sync)}</span>
          </p>
          <p className="text-[9px] font-mono text-[#A0A0A0]">
            {syncStatus?.total_models || 0} total · {syncStatus?.active_models || 0} active · {syncStatus?.deprecated_models || 0} deprecated
          </p>
          <p className="text-[8px] font-mono text-[#A0A0A0]">
            IQ via <a href="https://artificialanalysis.ai" target="_blank" rel="noopener noreferrer" className="underline hover:text-[#F5F1E8]">artificialanalysis.ai</a>
          </p>
        </div>
        <button onClick={doSync} disabled={syncing}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-mono border border-[#2A2A2A] text-[#00D4FF] hover:border-[#00D4FF] disabled:opacity-50 transition-colors">
          <RefreshCw className={`w-3 h-3 ${syncing ? 'animate-spin' : ''}`} />
          {syncing ? 'Syncing...' : 'Update from OpenRouter'}
        </button>
      </div>

      {syncResult && (
        <div className="p-2 bg-[#1A1A1A] border border-[#00D4FF40] text-xs font-mono">
          <span className="text-[#22C55E]">{syncResult.upserted || syncResult.added || 0} synced</span>
          {syncResult.deprecated > 0 && <>{' · '}<span className="text-[#F2A900]">{syncResult.deprecated} deprecated</span></>}
          {' · '}<span className="text-[#A0A0A0]">{syncResult.total} from OpenRouter</span>
          {syncResult.iq_matched != null && <>{' · '}<span className="text-[#F2A900]">{`IQ: ${syncResult.iq_matched}/${syncResult.iq_roster} matched`}</span></>}
        </div>
      )}

      {deprecatedCount > 0 && (
        <div className="flex items-center gap-2 p-2 bg-[#1A1A1A] border border-[#F2A900] text-xs">
          <AlertTriangle className="w-4 h-4 text-[#F2A900] flex-shrink-0" />
          <span className="text-[#F2A900]">{deprecatedCount} allowed {deprecatedCount === 1 ? 'model has' : 'models have'} been deprecated</span>
        </div>
      )}

      {enabled && (
        <>
          {error && <p className="text-[#EF4444] text-xs">{error}</p>}

          {/* ── Allowed Models with tier tabs + export (#588) ── */}
          <div className="p-3 bg-[#1A1A1A] border border-[#2A2A2A]">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-xs font-mono text-[#F2A900]">ALLOWED MODELS ({allowlist.length})</h3>
              <div className="flex items-center gap-1">
                {['xlsx', 'csv'].map(fmt => (
                  <button
                    key={fmt}
                    onClick={async () => {
                      try {
                        const res = await API.get(`/api/workspaces/${wsId}/model-allowlist/export?format=${fmt}`, { responseType: 'blob' });
                        const url = window.URL.createObjectURL(res.data);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = res.headers['content-disposition']?.match(/filename="?(.+)"?/)?.[1] || `models.${fmt}`;
                        a.click();
                        window.URL.revokeObjectURL(url);
                      } catch (e) { setError(e.response?.data?.detail || `Export failed: ${e.message}`); }
                    }}
                    className="px-2 py-1 text-[10px] font-mono text-[#A0A0A0] border border-[#2A2A2A] hover:border-[#FF4D00] hover:text-[#F5F1E8] transition-colors flex items-center gap-1"
                  >
                    <Download className="w-3 h-3" />
                    {fmt.toUpperCase()}
                  </button>
                ))}
              </div>
            </div>
            <TierTabs active={allowlistTierTab} onSelect={setAllowlistTierTab} counts={allowlistTierCounts} />
            {filteredAllowlist.length === 0 ? (
              <p className="text-xs text-[#A0A0A0] py-3">
                {allowlist.length === 0 ? 'No models allowed — all blocked. Select models below to add.' : 'No models in this tier.'}
              </p>
            ) : (
              /* #1044 — sticky header inside the same scroll container as the
                 rows so the scrollbar gutter affects both equally; prior
                 two-container layout inset body content by scrollbar width. */
              <div className="max-h-56 overflow-y-auto" style={{ scrollbarGutter: 'stable' }}>
                <div className="sticky top-0 bg-[#1A1A1A] z-10 flex items-center gap-2 px-2 py-1 text-[8px] font-mono text-[#A0A0A0] border-b border-[#2A2A2A]">
                  <span className="w-3 shrink-0" />
                  <SortTh field="display_name" label="MODEL" className="flex-1 text-left" />
                  <SortTh field="or_created_at" label="ADDED" className="w-12 text-right justify-end" />
                  <SortTh field="intelligence_score" label="IQ" className="w-9 text-right justify-end" />
                  <SortTh field="tier" label="TIER" className="w-14 text-right justify-end" />
                  <SortTh field="context_window" label="CTX" className="w-10 text-right justify-end" />
                  <SortTh field="max_output_tokens" label="OUT" className="w-9 text-right justify-end" />
                  <SortTh field="prompt_price_per_token" label="IN $/M" className="w-12 text-right justify-end" />
                  <SortTh field="completion_price_per_token" label="OUT $/M" className="w-12 text-right justify-end" />
                  <span className="w-6 shrink-0" />
                </div>
                <div className="space-y-0.5 mt-0.5">
                {filteredAllowlist.map(m => (
                  <div key={m.id} className={`flex items-center gap-2 px-2 py-1.5 bg-[#0F0F0F] border ${m.deprecated_at ? 'border-[#F2A900]' : 'border-[#2A2A2A]'}`}>
                    <Cpu className="w-3 h-3 text-[#A0A0A0] shrink-0" />
                    <span className="text-xs font-mono flex-1 truncate flex items-center gap-1.5" title={m.model_id}>
                      {m.display_name || m.model_id}
                      {isNew(m) && <span className="text-[7px] font-mono px-1 py-0.5 bg-[#22C55E]/20 text-[#22C55E] border border-[#22C55E]/40 shrink-0">NEW</span>}
                      {m.deprecated_at && (
                        <span className="flex items-center gap-1 text-[8px] font-mono text-[#F2A900] border border-[#F2A90040] px-1 py-0.5 shrink-0">
                          <AlertTriangle className="w-2.5 h-2.5" /> Deprecated
                        </span>
                      )}
                    </span>
                    <span className="w-12 text-right text-[8px] font-mono text-[#555] shrink-0"
                      title={m.or_created_at ? `OR added: ${new Date(m.or_created_at).toLocaleDateString()}` : 'OR date unknown'}>
                      {m.or_created_at ? new Date(m.or_created_at).toLocaleDateString('en-US', { month: 'short', year: '2-digit' }) : '—'}
                    </span>
                    <span className={`w-9 text-right text-[8px] font-mono shrink-0 ${m.intelligence_score != null ? 'text-[#F2A900]' : 'text-[#3A3A3A]'}`}
                      title="Intelligence Index — artificialanalysis.ai">
                      {fmtIq(m.intelligence_score)}
                    </span>
                    <span className="w-14 text-right shrink-0">
                      {m.tier && (
                        <span className="text-[8px] font-mono px-1 py-0.5 border" style={{
                          color: TIER_COLORS[m.tier] || '#A0A0A0',
                          borderColor: (TIER_COLORS[m.tier] || '#A0A0A0') + '40'
                        }}>{m.tier}</span>
                      )}
                    </span>
                    <span className="w-10 text-right text-[8px] font-mono text-[#A0A0A0] shrink-0">{fmtK(m.context_window)}</span>
                    <span className="w-9 text-right text-[8px] font-mono text-[#A0A0A0] shrink-0">{fmtK(m.max_output_tokens)}</span>
                    <span className="w-12 text-right text-[8px] font-mono text-[#A0A0A0] shrink-0"
                          title={fmtPriceTitle(m.prompt_price_per_token)}>{fmtPriceM(m.prompt_price_per_token)}</span>
                    <span className="w-12 text-right text-[8px] font-mono text-[#A0A0A0] shrink-0"
                          title={fmtPriceTitle(m.completion_price_per_token)}>{fmtPriceM(m.completion_price_per_token)}</span>
                    <button onClick={() => removeModel(m.id)} className="w-6 flex justify-center shrink-0 text-[#A0A0A0] hover:text-[#EF4444]" title="Remove from allowlist">
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
                </div>
              </div>
            )}
          </div>

          {/* ── Available Models with tier tabs + search + checkboxes ── */}
          <div className="p-3 bg-[#1A1A1A] border border-[#2A2A2A]">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-xs font-mono text-[#00D4FF]">AVAILABLE MODELS ({filteredRoster.length})</h3>
              {selected.size > 0 && (
                <button onClick={addSelected} disabled={adding}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-mono bg-[#FF4D00] text-white disabled:opacity-50 transition-colors">
                  <Check className="w-3 h-3" />
                  {adding ? 'Adding...' : `Add ${selected.size} selected`}
                </button>
              )}
            </div>

            {/* Tier tabs */}
            <TierTabs active={tierTab} onSelect={setTierTab} counts={availableTierCounts} />

            {/* Quick toggle filters (parity with admin Model Roster) */}
            <div className="flex gap-1.5 mb-3">
              <button onClick={() => setNewOnly(v => !v)}
                className={`px-2 py-0.5 text-[8px] font-mono border transition-colors ${
                  newOnly ? 'bg-[#22C55E] text-black border-[#22C55E]' : 'border-[#2A2A2A] text-[#A0A0A0] hover:border-[#22C55E] hover:text-[#F5F1E8]'}`}>
                New 30d
              </button>
              <button onClick={() => setHasIqOnly(v => !v)}
                className={`px-2 py-0.5 text-[8px] font-mono border transition-colors ${
                  hasIqOnly ? 'bg-[#F2A900] text-black border-[#F2A900]' : 'border-[#2A2A2A] text-[#A0A0A0] hover:border-[#F2A900] hover:text-[#F5F1E8]'}`}>
                Has IQ
              </button>
            </div>

            {/* Range filters + capabilities */}
            <div className="grid grid-cols-4 gap-2 mb-3">
              {[
                { label: 'Min IQ', state: minIq, set: setMinIq,
                  opts: [{v:0,l:'Any'},{v:200,l:'20+'},{v:300,l:'30+'},{v:400,l:'40+'},{v:500,l:'50+'}] },
                { label: 'Min Context', state: minCtx, set: setMinCtx,
                  opts: [{v:0,l:'Any'},{v:32000,l:'32k+'},{v:64000,l:'64k+'},{v:128000,l:'128k+'},{v:500000,l:'500k+'}] },
                { label: 'Min Output', state: minOut, set: setMinOut,
                  opts: [{v:0,l:'Any'},{v:4096,l:'4k+'},{v:8192,l:'8k+'},{v:16384,l:'16k+'},{v:32768,l:'32k+'}] },
                { label: 'Capabilities', custom: (
                  <div className="flex flex-wrap gap-1 mt-1.5">
                    {[['function_calling','Tools'],['thinking','Think'],['images','Images']].map(([v,l]) => (
                      <button key={v} onClick={() => setCapFilter(capFilter === v ? '' : v)}
                        className={`px-1.5 py-0.5 text-[8px] font-mono border transition-colors ${
                          capFilter === v ? 'bg-[#FF4D00] text-black border-[#FF4D00]' : 'border-[#2A2A2A] text-[#A0A0A0] hover:border-[#FF4D00] hover:text-[#F5F1E8]'}`}>
                        {l}
                      </button>
                    ))}
                  </div>
                )},
              ].map(({ label, state, set, opts, custom }) => (
                <div key={label} className="p-2 bg-[#0F0F0F] border border-[#2A2A2A]">
                  <p className="text-[8px] font-mono text-[#A0A0A0] mb-1.5">{label}</p>
                  {custom || (
                    <div className="flex flex-wrap gap-1">
                      {opts.map(o => (
                        <button key={o.v} onClick={() => set(o.v)}
                          className={`px-1.5 py-0.5 text-[8px] font-mono border transition-colors ${
                            state === o.v
                              ? 'bg-[#FF4D00] text-black border-[#FF4D00]'
                              : 'border-[#2A2A2A] text-[#A0A0A0] hover:border-[#FF4D00] hover:text-[#F5F1E8]'
                          }`}>
                          {o.l}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Search */}
            <div className="relative mb-3">
              <Search className="w-3 h-3 absolute left-2 top-2.5 text-[#A0A0A0]" />
              <input value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Type to search models..." className="w-full pl-7 pr-8 py-2 bg-[#0F0F0F] border border-[#2A2A2A] text-xs font-mono focus:outline-none focus:border-[#00D4FF]" />
              {search && (
                <button onClick={() => setSearch('')} className="absolute right-2 top-2 text-[#A0A0A0] hover:text-[#F5F1E8]">
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>

            {rosterLoading ? (
              <div className="flex items-center gap-2 text-[#A0A0A0] py-4"><Loader2 className="w-3 h-3 animate-spin" /> Loading roster...</div>
            ) : filteredRoster.length === 0 ? (
              <p className="text-xs text-[#A0A0A0] py-3">
                {roster.length === 0 ? 'No models in roster. Click "Update from OpenRouter" above.' : 'No models match.'}
              </p>
            ) : (
              <>
                <button onClick={toggleSelectAll}
                  className="flex items-center gap-2 w-full p-2 text-left text-xs font-mono text-[#A0A0A0] hover:text-[#F5F1E8] transition-colors">
                  {selected.size === filteredRoster.length
                    ? <CheckSquare className="w-3.5 h-3.5 text-[#FF4D00]" />
                    : <Square className="w-3.5 h-3.5" />}
                  {selected.size === filteredRoster.length ? 'Deselect all' : `Select all ${filteredRoster.length} visible`}
                </button>
                {/* #1044 — sticky header inside the scroll container */}
                <div className="max-h-64 overflow-y-auto" style={{ scrollbarGutter: 'stable' }}>
                  <div className="sticky top-0 bg-[#1A1A1A] z-10 flex items-center gap-2 px-2 py-1 text-[8px] font-mono text-[#A0A0A0] border-b border-[#2A2A2A]">
                    <span className="w-3.5 shrink-0" />
                    <SortTh field="display_name" label="MODEL" className="flex-1 text-left" />
                    <SortTh field="or_created_at" label="ADDED" className="w-12 text-right justify-end" />
                    <SortTh field="intelligence_score" label="IQ" className="w-9 text-right justify-end" />
                    <SortTh field="tier" label="TIER" className="w-14 text-right justify-end" />
                    <SortTh field="context_window" label="CTX" className="w-10 text-right justify-end" />
                    <SortTh field="max_output_tokens" label="OUT" className="w-9 text-right justify-end" />
                    <SortTh field="prompt_price_per_token" label="IN $/M" className="w-12 text-right justify-end" />
                    <SortTh field="completion_price_per_token" label="OUT $/M" className="w-12 text-right justify-end" />
                  </div>
                  <div className="space-y-1 mt-1">
                  {filteredRoster.slice(0, 100).map(m => (
                    <button key={m.slug} onClick={() => toggleSelect(m.slug)}
                      className={`flex items-center gap-2 w-full p-2 bg-[#0F0F0F] border text-left transition-colors ${
                        selected.has(m.slug) ? 'border-[#FF4D00]' : 'border-[#2A2A2A] hover:border-[#FF4D00]'
                      }`}>
                      {selected.has(m.slug)
                        ? <CheckSquare className="w-3.5 h-3.5 text-[#FF4D00] flex-shrink-0" />
                        : <Square className="w-3.5 h-3.5 text-[#A0A0A0] flex-shrink-0" />}
                      <span className="text-xs font-mono flex-1 truncate flex items-center gap-1.5" title={m.slug}>
                        {m.display_name || m.slug}
                        {isNew(m) && <span className="text-[7px] font-mono px-1 py-0.5 bg-[#22C55E]/20 text-[#22C55E] border border-[#22C55E]/40 shrink-0">NEW</span>}
                      </span>
                      <span className="text-[8px] font-mono text-[#555] w-12 text-right shrink-0"
                        title={m.or_created_at ? `OR added: ${new Date(m.or_created_at).toLocaleDateString()}` : 'OR date unknown'}>
                        {m.or_created_at ? new Date(m.or_created_at).toLocaleDateString('en-US', { month: 'short', year: '2-digit' }) : '—'}
                      </span>
                      <span className={`w-9 text-right text-[8px] font-mono shrink-0 ${m.intelligence_score != null ? 'text-[#F2A900]' : 'text-[#3A3A3A]'}`}
                        title="Intelligence Index — artificialanalysis.ai">
                        {fmtIq(m.intelligence_score)}
                      </span>
                      <span className="text-[8px] font-mono px-1.5 py-0.5 border shrink-0 w-14 text-center" style={{
                        color: TIER_COLORS[m.tier] || '#A0A0A0',
                        borderColor: (TIER_COLORS[m.tier] || '#A0A0A0') + '40'
                      }}>{m.tier}</span>
                      <span className="text-[8px] font-mono text-[#A0A0A0] w-10 text-right shrink-0" title="Context window">
                        {fmtK(m.context_window)}
                      </span>
                      <span className="text-[8px] font-mono text-[#A0A0A0] w-9 text-right shrink-0" title="Max output tokens">
                        {fmtK(m.max_output_tokens)}
                      </span>
                      <span className="text-[8px] font-mono text-[#A0A0A0] w-12 text-right shrink-0"
                        title={fmtPriceTitle(m.prompt_price_per_token)}>{fmtPriceM(m.prompt_price_per_token)}</span>
                      <span className="text-[8px] font-mono text-[#A0A0A0] w-12 text-right shrink-0"
                        title={fmtPriceTitle(m.completion_price_per_token)}>{fmtPriceM(m.completion_price_per_token)}</span>
                    </button>
                  ))}
                  {filteredRoster.length > 100 && (
                    <p className="text-[9px] text-[#A0A0A0] text-center py-1">Showing 100 of {filteredRoster.length} — refine search</p>
                  )}
                  </div>
                </div>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}
